import { useState, useEffect, useCallback } from 'react';
import { Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import API from '../api/axios';
import { FaHome, FaProjectDiagram, FaCogs, FaBriefcase, FaEnvelope, FaUser, FaSignOutAlt, FaPlus, FaEdit, FaTrash, FaShoppingBag, FaListAlt, FaUsers, FaChartBar, FaTag, FaBell, FaComments, FaCheck, FaTimes, FaClock, FaTicketAlt, FaBlog, FaQuoteLeft, FaQuestionCircle, FaCreditCard, FaPalette, FaWrench, FaFileAlt, FaAddressCard, FaPhoneAlt, FaFile, FaLayerGroup, FaSave } from 'react-icons/fa';

// Stats Dashboard with Revenue Chart + Quick Actions
const AdminHome = () => {
  const [stats, setStats] = useState({});
  useEffect(() => { API.get('/stats').then(r => setStats(r.data)).catch(console.error); }, []);

  const quickAction = async (orderId, status) => {
    try { await API.put(`/orders/${orderId}/status`, { status }); toast.success('Updated!'); API.get('/stats').then(r => setStats(r.data)); }
    catch { toast.error('Failed'); }
  };

  const cards = [
    { label: 'Total Orders', value: stats.totalOrders || 0, color: '#6c63ff', icon: <FaShoppingBag /> },
    { label: 'Pending', value: stats.pendingOrders || 0, color: '#ffc107', icon: <FaClock /> },
    { label: 'Completed', value: stats.completedOrders || 0, color: '#4caf50', icon: <FaCheck /> },
    { label: 'Revenue', value: `$${stats.totalRevenue || 0}`, color: '#00d2ff', icon: <FaChartBar /> },
    { label: 'Clients', value: stats.totalClients || 0, color: '#ff6584', icon: <FaUsers /> },
    { label: 'Services', value: stats.totalServices || 0, color: '#ffc371', icon: <FaListAlt /> },
    { label: 'Open Tickets', value: stats.openTickets || 0, color: '#ff9800', icon: <FaTicketAlt /> },
    { label: 'Unread Msgs', value: stats.unreadMessages || 0, color: '#9c27b0', icon: <FaEnvelope /> },
  ];

  // Revenue chart data
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const chartData = {
    labels: (stats.monthlyRevenue || []).map(m => months[(m._id.month - 1)] + ' ' + m._id.year),
    datasets: [
      { label: 'Revenue ($)', data: (stats.monthlyRevenue || []).map(m => m.total), borderColor: '#6c63ff', backgroundColor: 'rgba(108,99,255,0.1)', fill: true, tension: 0.4 },
      { label: 'Orders', data: (stats.monthlyRevenue || []).map(m => m.count), borderColor: '#00d2ff', backgroundColor: 'rgba(0,210,255,0.1)', fill: true, tension: 0.4, yAxisID: 'y1' }
    ]
  };

  let ChartComponent = null;
  try {
    const { Line } = require('react-chartjs-2');
    const { Chart, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler } = require('chart.js');
    Chart.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);
    ChartComponent = Line;
  } catch {}

  return (
    <div>
      <h1>Admin Dashboard</h1>
      {/* Stats Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px', marginTop: '20px' }}>
        {cards.map(c => (
          <div key={c.label} className="admin-card" style={{ textAlign: 'center', padding: '18px' }}>
            <div style={{ fontSize: '1.5rem', color: c.color, marginBottom: '5px' }}>{c.icon}</div>
            <div style={{ fontSize: '1.4rem', fontWeight: '700', color: c.color }}>{c.value}</div>
            <div style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>{c.label}</div>
          </div>
        ))}
      </div>

      {/* Revenue Chart */}
      {ChartComponent && (stats.monthlyRevenue || []).length > 0 && (
        <div className="admin-card" style={{ marginTop: '20px' }}>
          <h3 style={{ color: 'var(--primary)', marginBottom: '15px' }}><FaChartBar /> Monthly Revenue & Orders</h3>
          <div style={{ height: '300px' }}>
            <ChartComponent data={chartData} options={{
              responsive: true, maintainAspectRatio: false,
              plugins: { legend: { labels: { color: '#b8b8d4' } } },
              scales: {
                x: { ticks: { color: '#6c6c8a' }, grid: { color: 'rgba(108,99,255,0.1)' } },
                y: { ticks: { color: '#6c6c8a' }, grid: { color: 'rgba(108,99,255,0.1)' } },
                y1: { position: 'right', ticks: { color: '#6c6c8a' }, grid: { display: false } }
              }
            }} />
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: '20px', marginTop: '20px', flexWrap: 'wrap' }}>
        {/* Quick Actions - Recent Pending Orders */}
        <div className="admin-card" style={{ flex: 1, minWidth: '300px' }}>
          <h3 style={{ color: 'var(--primary)', marginBottom: '15px' }}><FaClock /> Quick Actions - Pending Orders</h3>
          {(stats.recentOrders || []).filter(o => ['pending', 'confirmed', 'in_progress', 'under_review'].includes(o.status)).slice(0, 5).map(order => (
            <div key={order._id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid var(--border)', flexWrap: 'wrap', gap: '8px' }}>
              <div>
                <strong style={{ fontSize: '0.85rem' }}>{order.orderNumber}</strong>
                <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem', marginLeft: '8px' }}>{order.client?.name}</span>
              </div>
              <div style={{ display: 'flex', gap: '5px' }}>
                {order.status === 'pending' && <button className="btn btn-primary btn-small" onClick={() => quickAction(order._id, 'confirmed')} style={{ padding: '4px 10px', fontSize: '0.75rem' }}><FaCheck /> Confirm</button>}
                {order.status === 'confirmed' && <button className="btn btn-primary btn-small" onClick={() => quickAction(order._id, 'in_progress')} style={{ padding: '4px 10px', fontSize: '0.75rem' }}>Start</button>}
                {order.status === 'in_progress' && <button className="btn btn-small" style={{ padding: '4px 10px', fontSize: '0.75rem', background: '#ff6584', color: 'white' }} onClick={() => quickAction(order._id, 'under_review')}>Review</button>}
                {(order.status === 'under_review') && <button className="btn btn-small" style={{ padding: '4px 10px', fontSize: '0.75rem', background: '#4caf50', color: 'white' }} onClick={() => quickAction(order._id, 'completed')}><FaCheck /> Complete</button>}
                <Link to={`/admin/orders/${order._id}`} className="btn btn-outline btn-small" style={{ padding: '4px 10px', fontSize: '0.75rem' }}><FaComments /></Link>
              </div>
            </div>
          ))}
          {(stats.recentOrders || []).filter(o => ['pending', 'confirmed', 'in_progress', 'under_review'].includes(o.status)).length === 0 && (
            <p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '15px' }}>No pending orders</p>
          )}
        </div>

        {/* Top Services */}
        <div className="admin-card" style={{ flex: 1, minWidth: '280px' }}>
          <h3 style={{ color: 'var(--primary)', marginBottom: '15px' }}><FaListAlt /> Top Services</h3>
          {(stats.topServices || []).map((s, i) => (
            <div key={s._id} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid var(--border)', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ width: '24px', height: '24px', borderRadius: '50%', background: 'var(--gradient-1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', color: 'white', fontWeight: '700' }}>{i + 1}</span>
                <span style={{ fontSize: '0.9rem' }}>{s.title}</span>
              </div>
              <span style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>{s.totalOrders} orders</span>
            </div>
          ))}
          {(stats.topServices || []).length === 0 && <p style={{ color: 'var(--text-muted)', textAlign: 'center' }}>No data yet</p>}
        </div>
      </div>
    </div>
  );
};

// Generic CRUD
const CrudSection = ({ title, apiPath, writeApiPath, fields, renderForm }) => {
  const writePath = writeApiPath || apiPath;
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [formData, setFormData] = useState({});
  const fetchItems = useCallback(async () => { try { const r = await API.get(apiPath); setItems(r.data); } catch (e) { console.error(e); } }, [apiPath]);
  useEffect(() => { fetchItems(); }, [fetchItems]);
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editItem) { await API.put(`${writePath}/${editItem._id}`, formData); toast.success(`${title} updated!`); }
      else { await API.post(writePath, formData); toast.success(`${title} added!`); }
      setShowForm(false); setEditItem(null); setFormData({}); fetchItems();
    } catch (err) { toast.error(err.response?.data?.message || 'Error'); }
  };
  const handleDelete = async (id) => { if (!window.confirm('Delete?')) return; try { await API.delete(`${writePath}/${id}`); toast.success('Deleted!'); fetchItems(); } catch { toast.error('Failed'); } };
  return (
    <div>
      <div className="admin-header"><h1>{title}s</h1><button className="btn btn-primary btn-small" onClick={() => { setShowForm(!showForm); setEditItem(null); setFormData({}); }}><FaPlus /> Add</button></div>
      {showForm && (
        <div className="admin-card"><h3>{editItem ? 'Edit' : 'Add'} {title}</h3>
          <form className="admin-form" onSubmit={handleSubmit} style={{ marginTop: '15px' }}>
            {renderForm(formData, setFormData)}
            <div style={{ display: 'flex', gap: '10px', marginTop: '15px' }}>
              <button type="submit" className="btn btn-primary btn-small">{editItem ? 'Update' : 'Save'}</button>
              <button type="button" className="btn btn-outline btn-small" onClick={() => setShowForm(false)}>Cancel</button>
            </div>
          </form>
        </div>
      )}
      <div className="admin-card">
        <table className="admin-table"><thead><tr>{fields.map(f => <th key={f.key}>{f.label}</th>)}<th>Actions</th></tr></thead>
          <tbody>
            {items.map(item => (<tr key={item._id}>{fields.map(f => <td key={f.key}>{f.render ? f.render(item[f.key], item) : (Array.isArray(item[f.key]) ? item[f.key].join(', ') : String(item[f.key] ?? ''))}</td>)}
              <td><button className="btn btn-outline btn-small" onClick={() => { setEditItem(item); setFormData(item); setShowForm(true); }} style={{ marginRight: '5px' }}><FaEdit /></button><button className="btn btn-danger btn-small" onClick={() => handleDelete(item._id)}><FaTrash /></button></td></tr>))}
            {items.length === 0 && <tr><td colSpan={fields.length + 1} style={{ textAlign: 'center', padding: '20px', color: 'var(--text-muted)' }}>Empty</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Orders Management
const OrdersManagement = () => {
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState('');
  const fetchOrders = useCallback(async () => { const q = filter ? `?status=${filter}` : ''; const r = await API.get(`/orders${q}`); setOrders(r.data); }, [filter]);
  useEffect(() => { fetchOrders(); }, [fetchOrders]);
  const statusColors = { pending: '#ffc107', confirmed: '#00d2ff', in_progress: '#6c63ff', under_review: '#ff6584', completed: '#4caf50', cancelled: '#f44336' };
  const updateStatus = async (id, status) => { try { await API.put(`/orders/${id}/status`, { status }); toast.success('Updated!'); fetchOrders(); } catch { toast.error('Failed'); } };
  const updatePayment = async (id, paymentStatus) => { try { await API.put(`/orders/${id}/payment`, { paymentStatus }); toast.success('Payment updated!'); fetchOrders(); } catch { toast.error('Failed'); } };

  return (
    <div>
      <div className="admin-header"><h1>Orders</h1></div>
      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' }}>
        {['', 'pending', 'confirmed', 'in_progress', 'under_review', 'completed', 'cancelled'].map(s => (
          <button key={s} onClick={() => setFilter(s)} className={`btn btn-small ${filter === s ? 'btn-primary' : 'btn-outline'}`}>{s || 'All'}</button>
        ))}
      </div>
      {orders.map(order => (
        <div key={order._id} className="admin-card">
          <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
            <div>
              <strong>{order.orderNumber}</strong> - {order.service?.title}
              <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>Client: {order.client?.name} ({order.client?.email})</p>
              <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>Payment: {order.paymentMethod} - <span style={{ color: order.paymentStatus === 'paid' ? '#4caf50' : '#ffc107' }}>{order.paymentStatus}</span></p>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '1.1rem', fontWeight: '700', color: 'var(--primary)' }}>${order.totalAmount}</div>
              <span style={{ padding: '3px 10px', borderRadius: '15px', fontSize: '0.75rem', background: `${statusColors[order.status]}20`, color: statusColors[order.status] }}>{order.status}</span>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px', marginTop: '12px', flexWrap: 'wrap' }}>
            {order.paymentStatus !== 'paid' && <button className="btn btn-small" style={{ background: '#4caf50', color: 'white' }} onClick={() => updatePayment(order._id, 'paid')}><FaCheck /> Confirm Payment</button>}
            {order.status === 'pending' && <button className="btn btn-small btn-primary" onClick={() => updateStatus(order._id, 'confirmed')}>Confirm</button>}
            {order.status === 'confirmed' && <button className="btn btn-small btn-primary" onClick={() => updateStatus(order._id, 'in_progress')}>Start Work</button>}
            {order.status === 'in_progress' && <button className="btn btn-small" style={{ background: '#ff6584', color: 'white' }} onClick={() => updateStatus(order._id, 'under_review')}>Send for Review</button>}
            {(order.status === 'under_review' || order.status === 'revision') && <button className="btn btn-small" style={{ background: '#4caf50', color: 'white' }} onClick={() => updateStatus(order._id, 'completed')}><FaCheck /> Complete</button>}
            {order.status !== 'completed' && order.status !== 'cancelled' && <button className="btn btn-small btn-danger" onClick={() => updateStatus(order._id, 'cancelled')}><FaTimes /></button>}
            <Link to={`/admin/orders/${order._id}`} className="btn btn-outline btn-small"><FaComments /> Chat</Link>
          </div>
        </div>
      ))}
    </div>
  );
};

// Order Detail (Admin side with chat)
const AdminOrderDetail = () => {
  const [order, setOrder] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [deliverables, setDeliverables] = useState('');
  const orderId = useLocation().pathname.split('/').pop();

  useEffect(() => {
    API.get(`/orders/${orderId}`).then(r => { setOrder(r.data); setDeliverables(r.data.deliverables || ''); }).catch(console.error);
    API.get(`/chat/${orderId}`).then(r => setChatMessages(r.data)).catch(console.error);
  }, [orderId]);

  const sendMessage = async () => {
    if (!newMessage.trim()) return;
    const res = await API.post('/chat', { orderId, message: newMessage });
    setChatMessages([...chatMessages, res.data]);
    setNewMessage('');
  };

  const saveDeliverables = async () => {
    try { await API.put(`/orders/${orderId}/status`, { status: order.status, deliverables }); toast.success('Saved!'); } catch { toast.error('Failed'); }
  };

  if (!order) return <div>Loading...</div>;

  return (
    <div>
      <h1>Order {order.orderNumber}</h1>
      <div style={{ display: 'flex', gap: '20px', marginTop: '20px', flexWrap: 'wrap' }}>
        <div style={{ flex: 2, minWidth: '300px' }}>
          <div className="admin-card">
            <h3 style={{ color: 'var(--primary)' }}>Client Info</h3>
            <p>Name: {order.client?.name}</p><p>Email: {order.client?.email}</p><p>Phone: {order.client?.phone}</p>
          </div>
          <div className="admin-card">
            <h3 style={{ color: 'var(--primary)' }}>Requirements</h3>
            <p style={{ color: 'var(--text-secondary)' }}>{order.requirements || 'None provided'}</p>
            {order.additionalNotes && <p style={{ color: 'var(--text-muted)', marginTop: '10px' }}>Notes: {order.additionalNotes}</p>}
          </div>
          <div className="admin-card">
            <h3 style={{ color: 'var(--primary)', marginBottom: '10px' }}>Deliverables</h3>
            <textarea value={deliverables} onChange={e => setDeliverables(e.target.value)} placeholder="Add deliverable links, files, notes..."
              style={{ width: '100%', minHeight: '100px', padding: '12px', background: 'var(--bg-dark)', border: '1px solid var(--border)', borderRadius: '12px', color: 'var(--text-primary)', fontFamily: 'Poppins' }} />
            <button onClick={saveDeliverables} className="btn btn-primary btn-small" style={{ marginTop: '10px' }}>Save Deliverables</button>
          </div>
          <div className="admin-card">
            <h3 style={{ color: 'var(--primary)', marginBottom: '15px' }}>Chat with Client</h3>
            <div style={{ maxHeight: '350px', overflowY: 'auto', marginBottom: '15px' }}>
              {chatMessages.map((msg, i) => (
                <div key={i} style={{ marginBottom: '10px', display: 'flex', justifyContent: msg.senderRole === 'admin' ? 'flex-end' : 'flex-start' }}>
                  <div style={{ maxWidth: '70%', padding: '10px 15px', borderRadius: '15px', background: msg.senderRole === 'admin' ? 'var(--gradient-1)' : 'var(--bg-dark)', color: 'white' }}>
                    <p style={{ fontSize: '0.85rem' }}>{msg.message}</p>
                    <span style={{ fontSize: '0.7rem', opacity: 0.7 }}>{new Date(msg.createdAt).toLocaleTimeString()}</span>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <input type="text" value={newMessage} onChange={e => setNewMessage(e.target.value)} placeholder="Reply..."
                onKeyDown={e => e.key === 'Enter' && sendMessage()}
                style={{ flex: 1, padding: '10px 15px', background: 'var(--bg-dark)', border: '1px solid var(--border)', borderRadius: '25px', color: 'var(--text-primary)', fontFamily: 'Poppins' }} />
              <button onClick={sendMessage} className="btn btn-primary btn-small" style={{ borderRadius: '25px' }}>Send</button>
            </div>
          </div>
        </div>
        <div style={{ flex: 1, minWidth: '250px' }}>
          <div className="admin-card">
            <h4>Payment: {order.paymentMethod}</h4>
            <p>TxID: {order.paymentDetails?.transactionId || order.paymentDetails?.cryptoTxHash || 'N/A'}</p>
            <p>Status: {order.paymentStatus}</p>
            <p>Amount: ${order.totalAmount}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Messages
const MessagesSection = () => {
  const [messages, setMessages] = useState([]);
  useEffect(() => { API.get('/messages').then(r => setMessages(r.data)).catch(console.error); }, []);
  const del = async (id) => { if (!window.confirm('Delete?')) return; await API.delete(`/messages/${id}`); setMessages(messages.filter(m => m._id !== id)); };
  return (
    <div>
      <div className="admin-header"><h1>Contact Messages</h1></div>
      {messages.map(msg => (
        <div key={msg._id} className="admin-card">
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <div><strong>{msg.name}</strong> &lt;{msg.email}&gt;<p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>{new Date(msg.createdAt).toLocaleDateString()}</p></div>
            <button className="btn btn-danger btn-small" onClick={() => del(msg._id)}><FaTrash /></button>
          </div>
          {msg.subject && <p style={{ color: 'var(--primary)' }}><strong>Subject:</strong> {msg.subject}</p>}
          <p style={{ color: 'var(--text-secondary)' }}>{msg.message}</p>
        </div>
      ))}
    </div>
  );
};

// Profile (Admin)
const ProfileSection = () => {
  const [profile, setProfile] = useState({ name: '', title: '', bio: '', email: '', phone: '', location: '', resumeUrl: '', social: { github: '', linkedin: '', twitter: '', facebook: '' } });
  useEffect(() => { API.get('/profile').then(r => setProfile(r.data)).catch(console.error); }, []);
  const save = async (e) => { e.preventDefault(); try { await API.put('/profile', profile); toast.success('Saved!'); } catch { toast.error('Failed'); } };
  return (
    <div>
      <div className="admin-header"><h1>Site Profile</h1></div>
      <div className="admin-card">
        <form className="admin-form" onSubmit={save}>
          <div className="form-row"><div className="form-group"><label>Name</label><input type="text" value={profile.name} onChange={e => setProfile({ ...profile, name: e.target.value })} /></div>
            <div className="form-group"><label>Title</label><input type="text" value={profile.title} onChange={e => setProfile({ ...profile, title: e.target.value })} /></div></div>
          <div className="form-group"><label>Bio</label><textarea value={profile.bio} onChange={e => setProfile({ ...profile, bio: e.target.value })} /></div>
          <div className="form-row"><div className="form-group"><label>Email</label><input type="email" value={profile.email} onChange={e => setProfile({ ...profile, email: e.target.value })} /></div>
            <div className="form-group"><label>Phone</label><input type="text" value={profile.phone} onChange={e => setProfile({ ...profile, phone: e.target.value })} /></div></div>
          <div className="form-row"><div className="form-group"><label>Location</label><input type="text" value={profile.location} onChange={e => setProfile({ ...profile, location: e.target.value })} /></div>
            <div className="form-group"><label>Resume URL</label><input type="text" value={profile.resumeUrl} onChange={e => setProfile({ ...profile, resumeUrl: e.target.value })} /></div></div>
          <h3 style={{ margin: '20px 0 10px', color: 'var(--primary)' }}>Social</h3>
          <div className="form-row"><div className="form-group"><label>GitHub</label><input type="text" value={profile.social?.github || ''} onChange={e => setProfile({ ...profile, social: { ...profile.social, github: e.target.value } })} /></div>
            <div className="form-group"><label>LinkedIn</label><input type="text" value={profile.social?.linkedin || ''} onChange={e => setProfile({ ...profile, social: { ...profile.social, linkedin: e.target.value } })} /></div></div>
          <button type="submit" className="btn btn-primary" style={{ marginTop: '15px' }}>Save</button>
        </form>
      </div>
    </div>
  );
};

// Select dropdown helper
const SelectInput = ({ value, onChange, options, ...props }) => (
  <select value={value} onChange={onChange} style={{ width: '100%', padding: '12px 18px', background: 'var(--bg-dark)', border: '1px solid var(--border)', borderRadius: '12px', color: 'var(--text-primary)', fontFamily: 'Poppins' }} {...props}>
    {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
  </select>
);

// Tickets Management
const TicketsManagement = () => {
  const [tickets, setTickets] = useState([]);
  const [filter, setFilter] = useState('');
  const fetchTickets = useCallback(async () => { const q = filter ? `?status=${filter}` : ''; const r = await API.get(`/tickets${q}`); setTickets(r.data); }, [filter]);
  useEffect(() => { fetchTickets(); }, [fetchTickets]);
  const statusColors = { open: '#ffc107', in_progress: '#6c63ff', waiting_client: '#ff6584', resolved: '#4caf50', closed: '#888' };
  const priorityColors = { low: '#888', medium: '#ffc107', high: '#ff6584', urgent: '#f44336' };

  return (
    <div>
      <div className="admin-header"><h1>Support Tickets</h1></div>
      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' }}>
        {['', 'open', 'in_progress', 'waiting_client', 'resolved', 'closed'].map(s => (
          <button key={s} onClick={() => setFilter(s)} className={`btn btn-small ${filter === s ? 'btn-primary' : 'btn-outline'}`}>{s ? s.replace('_', ' ') : 'All'}</button>
        ))}
      </div>
      {tickets.map(t => (
        <Link key={t._id} to={`/admin/tickets/${t._id}`} style={{ textDecoration: 'none' }}>
          <div className="admin-card" style={{ cursor: 'pointer' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '5px' }}>
                  <strong>{t.ticketNumber}</strong>
                  <span style={{ padding: '2px 10px', borderRadius: '15px', fontSize: '0.7rem', background: `${statusColors[t.status]}20`, color: statusColors[t.status] }}>{t.status.replace('_', ' ')}</span>
                  <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '0.7rem', background: `${priorityColors[t.priority]}20`, color: priorityColors[t.priority] }}>{t.priority}</span>
                </div>
                <p style={{ color: 'var(--text-secondary)' }}>{t.subject}</p>
                <p style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>Client: {t.client?.name} ({t.client?.email})</p>
              </div>
              <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>{new Date(t.createdAt).toLocaleDateString()}</span>
            </div>
          </div>
        </Link>
      ))}
      {tickets.length === 0 && <div className="admin-card" style={{ textAlign: 'center', color: 'var(--text-muted)' }}>No tickets</div>}
    </div>
  );
};

// Admin Ticket Detail
const AdminTicketDetail = () => {
  const [ticket, setTicket] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const ticketId = useLocation().pathname.split('/').pop();
  const fetchTicket = useCallback(async () => { try { setTicket((await API.get(`/tickets/${ticketId}`)).data); } catch {} }, [ticketId]);
  useEffect(() => { fetchTicket(); }, [fetchTicket]);
  const sendReply = async () => { if (!newMessage.trim()) return; await API.post(`/tickets/${ticketId}/message`, { message: newMessage }); setNewMessage(''); fetchTicket(); };
  const updateStatus = async (status) => { await API.put(`/tickets/${ticketId}/status`, { status }); toast.success('Updated!'); fetchTicket(); };

  if (!ticket) return <div>Loading...</div>;
  return (
    <div>
      <h1>{ticket.ticketNumber}</h1>
      <p style={{ color: 'var(--text-secondary)', marginBottom: '5px' }}><strong>Subject:</strong> {ticket.subject}</p>
      <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginBottom: '15px' }}>Client: {ticket.client?.name} | Status: {ticket.status} | Priority: {ticket.priority} | Category: {ticket.category}</p>
      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
        <button className="btn btn-small btn-primary" onClick={() => updateStatus('in_progress')}>In Progress</button>
        <button className="btn btn-small" style={{ background: '#4caf50', color: 'white' }} onClick={() => updateStatus('resolved')}>Resolve</button>
        <button className="btn btn-small" style={{ background: '#888', color: 'white' }} onClick={() => updateStatus('closed')}>Close</button>
      </div>
      <div className="admin-card">
        <div style={{ maxHeight: '400px', overflowY: 'auto', marginBottom: '15px' }}>
          {ticket.messages?.map((msg, i) => (
            <div key={i} style={{ marginBottom: '12px', display: 'flex', justifyContent: msg.senderRole === 'admin' ? 'flex-end' : 'flex-start' }}>
              <div style={{ maxWidth: '70%', padding: '12px 16px', borderRadius: '15px', background: msg.senderRole === 'admin' ? 'var(--gradient-1)' : 'var(--bg-dark)', color: 'white' }}>
                <p style={{ fontSize: '0.75rem', opacity: 0.7, marginBottom: '4px' }}>{msg.senderRole === 'admin' ? 'You (Admin)' : ticket.client?.name}</p>
                <p style={{ fontSize: '0.9rem' }}>{msg.message}</p>
                <span style={{ fontSize: '0.7rem', opacity: 0.6 }}>{new Date(msg.createdAt).toLocaleString()}</span>
              </div>
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <input type="text" value={newMessage} onChange={e => setNewMessage(e.target.value)} placeholder="Reply..." onKeyDown={e => e.key === 'Enter' && sendReply()}
            style={{ flex: 1, padding: '10px 15px', background: 'var(--bg-dark)', border: '1px solid var(--border)', borderRadius: '25px', color: 'var(--text-primary)', fontFamily: 'Poppins' }} />
          <button onClick={sendReply} className="btn btn-primary btn-small" style={{ borderRadius: '25px' }}>Send</button>
        </div>
      </div>
    </div>
  );
};

// Payment Configuration Page
const PaymentConfig = () => {
  const [settings, setSettings] = useState(null);
  useEffect(() => { API.get('/settings').then(r => setSettings(r.data)).catch(console.error); }, []);
  const save = async () => { try { await API.put('/settings/section/payment', settings.payment); toast.success('Payment settings saved!'); } catch { toast.error('Failed'); } };

  if (!settings) return <div>Loading...</div>;
  const p = settings.payment || {};
  const updateMethod = (method, field, value) => {
    setSettings(prev => ({ ...prev, payment: { ...prev.payment, [method]: { ...prev.payment[method], [field]: value } } }));
  };

  return (
    <div>
      <div className="admin-header"><h1><FaCreditCard /> Payment Configuration</h1></div>
      {['bkash', 'nagad', 'rocket'].map(method => (
        <div key={method} className="admin-card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
            <h3 style={{ color: 'var(--primary)', textTransform: 'capitalize' }}>{method}</h3>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input type="checkbox" checked={p[method]?.enabled || false} onChange={e => updateMethod(method, 'enabled', e.target.checked)} style={{ width: '18px', height: '18px', accentColor: 'var(--primary)' }} /> Enabled
            </label>
          </div>
          <div className="form-row">
            <div className="form-group"><label>Display Name</label><input type="text" value={p[method]?.name || ''} onChange={e => updateMethod(method, 'name', e.target.value)} /></div>
            <div className="form-group"><label>Number</label><input type="text" value={p[method]?.number || ''} onChange={e => updateMethod(method, 'number', e.target.value)} placeholder="01XXXXXXXXX" /></div>
            <div className="form-group"><label>Payment Type</label><input type="text" value={p[method]?.type || 'Send Money'} onChange={e => updateMethod(method, 'type', e.target.value)} /></div>
          </div>
          <div className="form-group"><label>Instructions</label><input type="text" value={p[method]?.instructions || ''} onChange={e => updateMethod(method, 'instructions', e.target.value)} placeholder="Additional instructions for clients" /></div>
        </div>
      ))}
      <div className="admin-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
          <h3 style={{ color: '#F7931A' }}>Crypto (USDT)</h3>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <input type="checkbox" checked={p.crypto?.enabled || false} onChange={e => updateMethod('crypto', 'enabled', e.target.checked)} style={{ width: '18px', height: '18px', accentColor: 'var(--primary)' }} /> Enabled
          </label>
        </div>
        <div className="form-row">
          <div className="form-group"><label>Display Name</label><input type="text" value={p.crypto?.name || ''} onChange={e => updateMethod('crypto', 'name', e.target.value)} /></div>
          <div className="form-group"><label>Network</label><input type="text" value={p.crypto?.network || 'TRC20'} onChange={e => updateMethod('crypto', 'network', e.target.value)} /></div>
          <div className="form-group"><label>Coin</label><input type="text" value={p.crypto?.coin || 'USDT'} onChange={e => updateMethod('crypto', 'coin', e.target.value)} /></div>
        </div>
        <div className="form-group"><label>Wallet Address</label><input type="text" value={p.crypto?.walletAddress || ''} onChange={e => updateMethod('crypto', 'walletAddress', e.target.value)} placeholder="TXXXXXXX..." /></div>
        <div className="form-group"><label>Instructions</label><input type="text" value={p.crypto?.instructions || ''} onChange={e => updateMethod('crypto', 'instructions', e.target.value)} /></div>
      </div>
      <div className="admin-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
          <h3 style={{ color: '#1a73e8' }}>Bank Transfer</h3>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <input type="checkbox" checked={p.bank?.enabled || false} onChange={e => updateMethod('bank', 'enabled', e.target.checked)} style={{ width: '18px', height: '18px', accentColor: 'var(--primary)' }} /> Enabled
          </label>
        </div>
        <div className="form-row">
          <div className="form-group"><label>Display Name</label><input type="text" value={p.bank?.name || ''} onChange={e => updateMethod('bank', 'name', e.target.value)} /></div>
          <div className="form-group"><label>Bank Name</label><input type="text" value={p.bank?.bankName || ''} onChange={e => updateMethod('bank', 'bankName', e.target.value)} /></div>
        </div>
        <div className="form-row">
          <div className="form-group"><label>Account Name</label><input type="text" value={p.bank?.accountName || ''} onChange={e => updateMethod('bank', 'accountName', e.target.value)} /></div>
          <div className="form-group"><label>Account Number</label><input type="text" value={p.bank?.accountNumber || ''} onChange={e => updateMethod('bank', 'accountNumber', e.target.value)} /></div>
          <div className="form-group"><label>Branch</label><input type="text" value={p.bank?.branch || ''} onChange={e => updateMethod('bank', 'branch', e.target.value)} /></div>
        </div>
        <div className="form-group"><label>Instructions</label><input type="text" value={p.bank?.instructions || ''} onChange={e => updateMethod('bank', 'instructions', e.target.value)} /></div>
      </div>
      <button onClick={save} className="btn btn-primary" style={{ marginTop: '10px' }}>Save Payment Settings</button>
    </div>
  );
};

// Site Settings
const SiteSettingsPage = () => {
  const [settings, setSettings] = useState(null);
  useEffect(() => { API.get('/settings').then(r => setSettings(r.data)).catch(console.error); }, []);
  const save = async () => { try { await API.put('/settings', settings); toast.success('Saved!'); } catch { toast.error('Failed'); } };
  if (!settings) return <div>Loading...</div>;

  return (
    <div>
      <div className="admin-header"><h1><FaWrench /> Site Settings</h1></div>
      <div className="admin-card">
        <h3 style={{ color: 'var(--primary)', marginBottom: '15px' }}>General</h3>
        <div className="form-row">
          <div className="form-group"><label>Site Name</label><input type="text" value={settings.siteName || ''} onChange={e => setSettings({ ...settings, siteName: e.target.value })} /></div>
          <div className="form-group"><label>Currency Symbol</label><input type="text" value={settings.currency?.symbol || '$'} onChange={e => setSettings({ ...settings, currency: { ...settings.currency, symbol: e.target.value } })} /></div>
          <div className="form-group"><label>Currency Code</label><input type="text" value={settings.currency?.code || 'USD'} onChange={e => setSettings({ ...settings, currency: { ...settings.currency, code: e.target.value } })} /></div>
        </div>
        <div className="form-group"><label>Site Description</label><textarea value={settings.siteDescription || ''} onChange={e => setSettings({ ...settings, siteDescription: e.target.value })} /></div>
      </div>
      <div className="admin-card">
        <h3 style={{ color: 'var(--primary)', marginBottom: '15px' }}>Email (SMTP)</h3>
        <div className="form-row">
          <div className="form-group"><label>SMTP Host</label><input type="text" value={settings.email?.smtpHost || ''} onChange={e => setSettings({ ...settings, email: { ...settings.email, smtpHost: e.target.value } })} placeholder="smtp.gmail.com" /></div>
          <div className="form-group"><label>SMTP Port</label><input type="number" value={settings.email?.smtpPort || 587} onChange={e => setSettings({ ...settings, email: { ...settings.email, smtpPort: Number(e.target.value) } })} /></div>
        </div>
        <div className="form-row">
          <div className="form-group"><label>SMTP User</label><input type="text" value={settings.email?.smtpUser || ''} onChange={e => setSettings({ ...settings, email: { ...settings.email, smtpUser: e.target.value } })} /></div>
          <div className="form-group"><label>SMTP Password</label><input type="password" value={settings.email?.smtpPass || ''} onChange={e => setSettings({ ...settings, email: { ...settings.email, smtpPass: e.target.value } })} /></div>
        </div>
        <div className="form-row">
          <div className="form-group"><label>From Email</label><input type="text" value={settings.email?.fromEmail || ''} onChange={e => setSettings({ ...settings, email: { ...settings.email, fromEmail: e.target.value } })} /></div>
          <div className="form-group"><label>From Name</label><input type="text" value={settings.email?.fromName || ''} onChange={e => setSettings({ ...settings, email: { ...settings.email, fromName: e.target.value } })} /></div>
        </div>
      </div>
      <div className="admin-card">
        <h3 style={{ color: 'var(--primary)', marginBottom: '15px' }}>SEO</h3>
        <div className="form-group"><label>Meta Title</label><input type="text" value={settings.seo?.metaTitle || ''} onChange={e => setSettings({ ...settings, seo: { ...settings.seo, metaTitle: e.target.value } })} /></div>
        <div className="form-group"><label>Meta Description</label><textarea value={settings.seo?.metaDescription || ''} onChange={e => setSettings({ ...settings, seo: { ...settings.seo, metaDescription: e.target.value } })} /></div>
        <div className="form-group"><label>Google Analytics ID</label><input type="text" value={settings.seo?.googleAnalytics || ''} onChange={e => setSettings({ ...settings, seo: { ...settings.seo, googleAnalytics: e.target.value } })} placeholder="G-XXXXXXX" /></div>
      </div>
      <div className="admin-card">
        <h3 style={{ color: 'var(--primary)', marginBottom: '15px' }}>Social Links</h3>
        <div className="form-row">
          <div className="form-group"><label>Facebook</label><input type="text" value={settings.social?.facebook || ''} onChange={e => setSettings({ ...settings, social: { ...settings.social, facebook: e.target.value } })} /></div>
          <div className="form-group"><label>Twitter</label><input type="text" value={settings.social?.twitter || ''} onChange={e => setSettings({ ...settings, social: { ...settings.social, twitter: e.target.value } })} /></div>
        </div>
        <div className="form-row">
          <div className="form-group"><label>Instagram</label><input type="text" value={settings.social?.instagram || ''} onChange={e => setSettings({ ...settings, social: { ...settings.social, instagram: e.target.value } })} /></div>
          <div className="form-group"><label>YouTube</label><input type="text" value={settings.social?.youtube || ''} onChange={e => setSettings({ ...settings, social: { ...settings.social, youtube: e.target.value } })} /></div>
        </div>
        <div className="form-row">
          <div className="form-group"><label>Telegram</label><input type="text" value={settings.social?.telegram || ''} onChange={e => setSettings({ ...settings, social: { ...settings.social, telegram: e.target.value } })} /></div>
          <div className="form-group"><label>WhatsApp</label><input type="text" value={settings.social?.whatsapp || ''} onChange={e => setSettings({ ...settings, social: { ...settings.social, whatsapp: e.target.value } })} /></div>
        </div>
      </div>
      <div className="admin-card">
        <h3 style={{ color: 'var(--primary)', marginBottom: '15px' }}>Referral Program</h3>
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '15px', cursor: 'pointer' }}>
          <input type="checkbox" checked={settings.referral?.enabled || false} onChange={e => setSettings({ ...settings, referral: { ...settings.referral, enabled: e.target.checked } })} style={{ width: '18px', height: '18px', accentColor: 'var(--primary)' }} /> Enable Referral Program
        </label>
        <div className="form-row">
          <div className="form-group"><label>Discount % for referred user</label><input type="number" value={settings.referral?.discountPercent || 10} onChange={e => setSettings({ ...settings, referral: { ...settings.referral, discountPercent: Number(e.target.value) } })} /></div>
          <div className="form-group"><label>Reward $ for referrer</label><input type="number" value={settings.referral?.referrerReward || 5} onChange={e => setSettings({ ...settings, referral: { ...settings.referral, referrerReward: Number(e.target.value) } })} /></div>
        </div>
      </div>
      <div className="admin-card">
        <h3 style={{ color: 'var(--secondary)', marginBottom: '15px' }}>Maintenance Mode</h3>
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '15px', cursor: 'pointer' }}>
          <input type="checkbox" checked={settings.maintenance?.enabled || false} onChange={e => setSettings({ ...settings, maintenance: { ...settings.maintenance, enabled: e.target.checked } })} style={{ width: '18px', height: '18px', accentColor: '#ff4757' }} /> Enable Maintenance Mode
        </label>
        <div className="form-group"><label>Maintenance Message</label><textarea value={settings.maintenance?.message || ''} onChange={e => setSettings({ ...settings, maintenance: { ...settings.maintenance, message: e.target.value } })} /></div>
      </div>
      <button onClick={save} className="btn btn-primary" style={{ marginTop: '10px' }}>Save All Settings</button>
    </div>
  );
};

// Appearance Settings
const AppearancePage = () => {
  const [settings, setSettings] = useState(null);
  useEffect(() => { API.get('/settings').then(r => setSettings(r.data)).catch(console.error); }, []);
  const save = async () => { try { await API.put('/settings/section/appearance', settings.appearance); toast.success('Saved!'); } catch { toast.error('Failed'); } };
  if (!settings) return <div>Loading...</div>;
  const a = settings.appearance || {};
  const updateApp = (k, v) => setSettings(prev => ({ ...prev, appearance: { ...prev.appearance, [k]: v } }));

  return (
    <div>
      <div className="admin-header"><h1><FaPalette /> Appearance</h1></div>
      <div className="admin-card">
        <h3 style={{ color: 'var(--primary)', marginBottom: '15px' }}>Colors</h3>
        <div className="form-row">
          <div className="form-group"><label>Primary Color</label>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <input type="color" value={a.primaryColor || '#6c63ff'} onChange={e => updateApp('primaryColor', e.target.value)} style={{ width: '50px', height: '40px', border: 'none', cursor: 'pointer' }} />
              <input type="text" value={a.primaryColor || '#6c63ff'} onChange={e => updateApp('primaryColor', e.target.value)} />
            </div>
          </div>
          <div className="form-group"><label>Secondary Color</label>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <input type="color" value={a.secondaryColor || '#ff6584'} onChange={e => updateApp('secondaryColor', e.target.value)} style={{ width: '50px', height: '40px', border: 'none', cursor: 'pointer' }} />
              <input type="text" value={a.secondaryColor || '#ff6584'} onChange={e => updateApp('secondaryColor', e.target.value)} />
            </div>
          </div>
          <div className="form-group"><label>Accent Color</label>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <input type="color" value={a.accentColor || '#00d2ff'} onChange={e => updateApp('accentColor', e.target.value)} style={{ width: '50px', height: '40px', border: 'none', cursor: 'pointer' }} />
              <input type="text" value={a.accentColor || '#00d2ff'} onChange={e => updateApp('accentColor', e.target.value)} />
            </div>
          </div>
        </div>
      </div>
      <div className="admin-card">
        <h3 style={{ color: 'var(--primary)', marginBottom: '15px' }}>Hero Section</h3>
        <div className="form-group"><label>Hero Title</label><input type="text" value={a.heroTitle || ''} onChange={e => updateApp('heroTitle', e.target.value)} placeholder="Override hero title" /></div>
        <div className="form-group"><label>Hero Subtitle</label><input type="text" value={a.heroSubtitle || ''} onChange={e => updateApp('heroSubtitle', e.target.value)} placeholder="Override hero subtitle" /></div>
      </div>
      <div className="admin-card">
        <h3 style={{ color: 'var(--primary)', marginBottom: '15px' }}>Footer</h3>
        <div className="form-group"><label>Footer Text</label><input type="text" value={a.footerText || ''} onChange={e => updateApp('footerText', e.target.value)} /></div>
      </div>
      <div className="admin-card">
        <h3 style={{ color: 'var(--primary)', marginBottom: '15px' }}>Custom CSS</h3>
        <div className="form-group"><textarea value={a.customCSS || ''} onChange={e => updateApp('customCSS', e.target.value)} placeholder="Add custom CSS here..." style={{ minHeight: '150px', fontFamily: 'monospace' }} /></div>
      </div>
      <button onClick={save} className="btn btn-primary" style={{ marginTop: '10px' }}>Save Appearance</button>
    </div>
  );
};

// ============ PAGE-BY-PAGE MANAGEMENT ============

// Generic Page Editor
const PageEditor = ({ pageName, title, icon, children }) => {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { API.get(`/pages/${pageName}`).then(r => setData(r.data.sections)).catch(console.error); }, [pageName]);

  const save = async () => {
    setSaving(true);
    try { await API.put(`/pages/${pageName}`, { sections: data }); toast.success(`${title} page saved!`); }
    catch { toast.error('Failed to save'); }
    setSaving(false);
  };

  if (!data) return <div style={{ color: 'var(--text-muted)', padding: '40px', textAlign: 'center' }}>Loading...</div>;

  return (
    <div>
      <div className="admin-header">
        <h1>{icon} {title} Page Settings</h1>
        <button className="btn btn-primary" onClick={save} disabled={saving}><FaSave /> {saving ? 'Saving...' : 'Save Changes'}</button>
      </div>
      {children(data, setData)}
    </div>
  );
};

// Helper: array item editor
const ArrayEditor = ({ items, setItems, fields, title }) => {
  const addItem = () => setItems([...items, fields.reduce((acc, f) => ({ ...acc, [f.key]: f.default || '' }), {})]);
  const updateItem = (idx, key, val) => { const n = [...items]; n[idx] = { ...n[idx], [key]: val }; setItems(n); };
  const removeItem = (idx) => setItems(items.filter((_, i) => i !== idx));
  const moveItem = (idx, dir) => {
    const n = [...items]; const target = idx + dir;
    if (target < 0 || target >= n.length) return;
    [n[idx], n[target]] = [n[target], n[idx]]; setItems(n);
  };

  return (
    <div className="admin-card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
        <h3 style={{ color: 'var(--primary)' }}>{title}</h3>
        <button className="btn btn-primary btn-small" onClick={addItem}><FaPlus /> Add</button>
      </div>
      {items.map((item, idx) => (
        <div key={idx} style={{ padding: '15px', background: 'var(--bg-dark)', borderRadius: '12px', marginBottom: '10px', border: '1px solid var(--border)' }}>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            {fields.map(f => (
              <div key={f.key} className="form-group" style={{ flex: f.flex || 1, minWidth: '120px', marginBottom: '8px' }}>
                <label style={{ fontSize: '0.8rem' }}>{f.label}</label>
                {f.type === 'textarea' ? (
                  <textarea value={item[f.key] || ''} onChange={e => updateItem(idx, f.key, e.target.value)} style={{ minHeight: '60px' }} />
                ) : f.type === 'color' ? (
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input type="color" value={item[f.key] || '#6c63ff'} onChange={e => updateItem(idx, f.key, e.target.value)} style={{ width: '40px', height: '36px', border: 'none', cursor: 'pointer' }} />
                    <input type="text" value={item[f.key] || ''} onChange={e => updateItem(idx, f.key, e.target.value)} />
                  </div>
                ) : (
                  <input type={f.type || 'text'} value={item[f.key] || ''} onChange={e => updateItem(idx, f.key, e.target.value)} />
                )}
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: '5px', marginTop: '5px' }}>
            <button className="btn btn-outline btn-small" onClick={() => moveItem(idx, -1)} title="Move up">↑</button>
            <button className="btn btn-outline btn-small" onClick={() => moveItem(idx, 1)} title="Move down">↓</button>
            <button className="btn btn-danger btn-small" onClick={() => removeItem(idx)}><FaTrash /></button>
          </div>
        </div>
      ))}
      {items.length === 0 && <p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '15px' }}>No items. Click "Add" to create one.</p>}
    </div>
  );
};

// HOME PAGE MANAGER
const HomePageManager = () => (
  <PageEditor pageName="home" title="Home" icon={<FaHome />}>
    {(data, setData) => (
      <>
        <div className="admin-card">
          <h3 style={{ color: 'var(--primary)', marginBottom: '15px' }}>Hero Section</h3>
          <div className="form-group"><label>Greeting Text</label>
            <input type="text" value={data.hero?.greeting || ''} onChange={e => setData({ ...data, hero: { ...data.hero, greeting: e.target.value } })} placeholder="👋 Hello, I'm" /></div>
          <div className="form-group"><label>Typing Words (one per line)</label>
            <textarea value={(data.hero?.typingWords || []).join('\n')} onChange={e => setData({ ...data, hero: { ...data.hero, typingWords: e.target.value.split('\n').filter(w => w.trim()) } })}
              placeholder="Full Stack Web Developer&#10;MERN Stack Expert&#10;WordPress Developer" style={{ minHeight: '100px' }} /></div>
        </div>
        <ArrayEditor title="Stats Counter" items={data.stats?.items || []} setItems={items => setData({ ...data, stats: { ...data.stats, items } })}
          fields={[{ key: 'value', label: 'Value', default: '0+' }, { key: 'label', label: 'Label', default: 'Label' }]} />
        <div className="admin-card">
          <h3 style={{ color: 'var(--primary)', marginBottom: '15px' }}>Show/Hide Sections</h3>
          {[['showServices', 'Services Section'], ['showSkills', 'Skills Section'], ['showProjects', 'Projects Section'], ['showExperience', 'Experience Section'], ['showTestimonials', 'Testimonials Section'], ['showFAQ', 'FAQ Section'], ['showContact', 'Contact Section']].map(([key, label]) => (
            <label key={key} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 0', cursor: 'pointer' }}>
              <input type="checkbox" checked={data[key] !== false} onChange={e => setData({ ...data, [key]: e.target.checked })} style={{ width: '18px', height: '18px', accentColor: 'var(--primary)' }} />
              <span style={{ color: 'var(--text-secondary)' }}>{label}</span>
            </label>
          ))}
        </div>
      </>
    )}
  </PageEditor>
);

// ABOUT PAGE MANAGER
const AboutPageManager = () => (
  <PageEditor pageName="about" title="About" icon={<FaAddressCard />}>
    {(data, setData) => (
      <>
        <div className="admin-card">
          <h3 style={{ color: 'var(--primary)', marginBottom: '15px' }}>Page Header</h3>
          <div className="form-row">
            <div className="form-group"><label>Title</label><input type="text" value={data.title || ''} onChange={e => setData({ ...data, title: e.target.value })} /></div>
            <div className="form-group"><label>Subtitle</label><input type="text" value={data.subtitle || ''} onChange={e => setData({ ...data, subtitle: e.target.value })} /></div>
          </div>
        </div>
        <ArrayEditor title="Expertise Cards" items={data.expertiseCards || []} setItems={items => setData({ ...data, expertiseCards: items })}
          fields={[{ key: 'label', label: 'Label' }, { key: 'desc', label: 'Description', flex: 2 }, { key: 'icon', label: 'Icon Name (e.g. FaCode)' }, { key: 'color', label: 'Color', type: 'color' }]} />
        <div className="admin-card">
          <h3 style={{ color: 'var(--primary)', marginBottom: '15px' }}>Checklist Items (one per line)</h3>
          <textarea value={(data.checklist || []).join('\n')} onChange={e => setData({ ...data, checklist: e.target.value.split('\n').filter(w => w.trim()) })}
            style={{ width: '100%', minHeight: '200px', padding: '12px', background: 'var(--bg-dark)', border: '1px solid var(--border)', borderRadius: '12px', color: 'var(--text-primary)', fontFamily: 'Poppins' }}
            placeholder="Custom Web Application Development&#10;Responsive Design&#10;..." />
        </div>
        <ArrayEditor title="Stats" items={data.stats || []} setItems={items => setData({ ...data, stats: items })}
          fields={[{ key: 'value', label: 'Value' }, { key: 'label', label: 'Label' }]} />
      </>
    )}
  </PageEditor>
);

// CONTACT PAGE MANAGER
const ContactPageManager = () => (
  <PageEditor pageName="contact" title="Contact" icon={<FaPhoneAlt />}>
    {(data, setData) => (
      <>
        <div className="admin-card">
          <h3 style={{ color: 'var(--primary)', marginBottom: '15px' }}>Page Content</h3>
          <div className="form-row">
            <div className="form-group"><label>Page Title</label><input type="text" value={data.title || ''} onChange={e => setData({ ...data, title: e.target.value })} /></div>
            <div className="form-group"><label>Page Subtitle</label><input type="text" value={data.subtitle || ''} onChange={e => setData({ ...data, subtitle: e.target.value })} /></div>
          </div>
          <div className="form-group"><label>Section Heading</label><input type="text" value={data.heading || ''} onChange={e => setData({ ...data, heading: e.target.value })} /></div>
          <div className="form-group"><label>Description</label><textarea value={data.description || ''} onChange={e => setData({ ...data, description: e.target.value })} /></div>
          <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', marginTop: '10px' }}>
            <input type="checkbox" checked={data.showFAQ !== false} onChange={e => setData({ ...data, showFAQ: e.target.checked })} style={{ width: '18px', height: '18px', accentColor: 'var(--primary)' }} />
            <span style={{ color: 'var(--text-secondary)' }}>Show FAQ Section</span>
          </label>
        </div>
      </>
    )}
  </PageEditor>
);

// RESUME PAGE MANAGER
const ResumePageManager = () => (
  <PageEditor pageName="resume" title="Resume" icon={<FaFile />}>
    {(data, setData) => (
      <>
        <div className="admin-card">
          <h3 style={{ color: 'var(--primary)', marginBottom: '15px' }}>Page Header</h3>
          <div className="form-row">
            <div className="form-group"><label>Title</label><input type="text" value={data.title || ''} onChange={e => setData({ ...data, title: e.target.value })} /></div>
            <div className="form-group"><label>Subtitle</label><input type="text" value={data.subtitle || ''} onChange={e => setData({ ...data, subtitle: e.target.value })} /></div>
          </div>
        </div>
        <div className="admin-card">
          <h3 style={{ color: 'var(--primary)', marginBottom: '15px' }}>Resume Content</h3>
          <div className="form-group"><label>Professional Summary</label>
            <textarea value={data.summary || ''} onChange={e => setData({ ...data, summary: e.target.value })} style={{ minHeight: '120px' }}
              placeholder="Experienced Full Stack Developer..." /></div>
        </div>
        <div className="admin-card">
          <h3 style={{ color: 'var(--primary)', marginBottom: '15px' }}>Services Offered (one per line)</h3>
          <textarea value={(data.servicesOffered || []).join('\n')} onChange={e => setData({ ...data, servicesOffered: e.target.value.split('\n').filter(w => w.trim()) })}
            style={{ width: '100%', minHeight: '180px', padding: '12px', background: 'var(--bg-dark)', border: '1px solid var(--border)', borderRadius: '12px', color: 'var(--text-primary)', fontFamily: 'Poppins' }}
            placeholder="MERN Stack Development&#10;WordPress Development&#10;..." />
        </div>
        <div className="admin-card" style={{ background: 'rgba(108,99,255,0.05)' }}>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>
            <strong>Note:</strong> Resume automatically pulls data from: Profile (name, title, bio, contact), Skills, Experience, and Projects sections. Update those from their respective admin pages.
          </p>
        </div>
      </>
    )}
  </PageEditor>
);

// SERVICES PAGE MANAGER
const ServicesPageManager = () => (
  <PageEditor pageName="services" title="Services" icon={<FaListAlt />}>
    {(data, setData) => (
      <>
        <div className="admin-card">
          <h3 style={{ color: 'var(--primary)', marginBottom: '15px' }}>Page Header</h3>
          <div className="form-row">
            <div className="form-group"><label>Title</label><input type="text" value={data.title || ''} onChange={e => setData({ ...data, title: e.target.value })} /></div>
            <div className="form-group"><label>Subtitle</label><input type="text" value={data.subtitle || ''} onChange={e => setData({ ...data, subtitle: e.target.value })} /></div>
          </div>
        </div>
        <div className="admin-card" style={{ background: 'rgba(108,99,255,0.05)' }}>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>
            <strong>Manage Services:</strong> Go to <Link to="/admin/services" style={{ color: 'var(--primary)' }}>Services CRUD</Link> to add, edit, or delete individual services.
            <br /><strong>Categories:</strong> Go to <Link to="/admin/categories" style={{ color: 'var(--primary)' }}>Categories</Link> to manage service categories.
          </p>
        </div>
      </>
    )}
  </PageEditor>
);

// PROJECTS PAGE MANAGER
const ProjectsPageManager = () => (
  <PageEditor pageName="projects" title="Projects" icon={<FaProjectDiagram />}>
    {(data, setData) => (
      <>
        <div className="admin-card">
          <h3 style={{ color: 'var(--primary)', marginBottom: '15px' }}>Page Header</h3>
          <div className="form-row">
            <div className="form-group"><label>Title</label><input type="text" value={data.title || ''} onChange={e => setData({ ...data, title: e.target.value })} /></div>
            <div className="form-group"><label>Subtitle</label><input type="text" value={data.subtitle || ''} onChange={e => setData({ ...data, subtitle: e.target.value })} /></div>
          </div>
        </div>
        <div className="admin-card" style={{ background: 'rgba(108,99,255,0.05)' }}>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>
            <strong>Manage Projects:</strong> Go to <Link to="/admin/projects" style={{ color: 'var(--primary)' }}>Projects CRUD</Link> to add, edit, or delete projects with live demo URLs and GitHub links.
          </p>
        </div>
      </>
    )}
  </PageEditor>
);

// BLOG PAGE MANAGER
const BlogPageManager = () => (
  <PageEditor pageName="blog" title="Blog" icon={<FaBlog />}>
    {(data, setData) => (
      <>
        <div className="admin-card">
          <h3 style={{ color: 'var(--primary)', marginBottom: '15px' }}>Page Header</h3>
          <div className="form-row">
            <div className="form-group"><label>Title</label><input type="text" value={data.title || ''} onChange={e => setData({ ...data, title: e.target.value })} /></div>
            <div className="form-group"><label>Subtitle</label><input type="text" value={data.subtitle || ''} onChange={e => setData({ ...data, subtitle: e.target.value })} /></div>
          </div>
        </div>
        <div className="admin-card" style={{ background: 'rgba(108,99,255,0.05)' }}>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>
            <strong>Manage Posts:</strong> Go to <Link to="/admin/blog" style={{ color: 'var(--primary)' }}>Blog CRUD</Link> to write, edit, publish, or delete blog posts.
          </p>
        </div>
      </>
    )}
  </PageEditor>
);

// Main Admin Dashboard
const AdminDashboard = () => {
  const { user, logout, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [categories, setCategories] = useState([]);

  useEffect(() => { if (!loading && (!user || user.role !== 'admin')) navigate('/admin/login'); }, [user, loading, navigate]);
  useEffect(() => { API.get('/categories').then(r => setCategories(r.data)).catch(() => {}); }, []);

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', color: 'var(--primary)' }}>Loading...</div>;
  if (!user) return null;

  const isActive = (path) => location.pathname === path ? 'active' : '';

  return (
    <div className="admin-layout">
      <aside className="admin-sidebar">
        <h2>Admin Panel</h2>
        <Link to="/admin" className={isActive('/admin')}><FaHome /> Dashboard</Link>
        <Link to="/admin/orders" className={isActive('/admin/orders')}><FaShoppingBag /> Orders</Link>
        <Link to="/admin/services" className={isActive('/admin/services')}><FaListAlt /> Services</Link>
        <Link to="/admin/categories" className={isActive('/admin/categories')}><FaCogs /> Categories</Link>
        <Link to="/admin/coupons" className={isActive('/admin/coupons')}><FaTag /> Coupons</Link>
        <Link to="/admin/clients" className={isActive('/admin/clients')}><FaUsers /> Clients</Link>
        <Link to="/admin/tickets" className={isActive('/admin/tickets')}><FaTicketAlt /> Tickets</Link>
        <Link to="/admin/messages" className={isActive('/admin/messages')}><FaEnvelope /> Messages</Link>
        <div style={{ borderTop: '1px solid var(--border)', margin: '10px 0', paddingTop: '5px' }}><small style={{ color: 'var(--text-muted)', paddingLeft: '15px' }}>Pages</small></div>
        <Link to="/admin/page/home" className={isActive('/admin/page/home')}><FaHome /> Home Page</Link>
        <Link to="/admin/page/about" className={isActive('/admin/page/about')}><FaAddressCard /> About Page</Link>
        <Link to="/admin/page/contact" className={isActive('/admin/page/contact')}><FaPhoneAlt /> Contact Page</Link>
        <Link to="/admin/page/resume" className={isActive('/admin/page/resume')}><FaFile /> Resume Page</Link>
        <Link to="/admin/page/services" className={isActive('/admin/page/services')}><FaListAlt /> Services Page</Link>
        <Link to="/admin/page/projects" className={isActive('/admin/page/projects')}><FaProjectDiagram /> Projects Page</Link>
        <Link to="/admin/page/blog" className={isActive('/admin/page/blog')}><FaBlog /> Blog Page</Link>
        <div style={{ borderTop: '1px solid var(--border)', margin: '10px 0', paddingTop: '5px' }}><small style={{ color: 'var(--text-muted)', paddingLeft: '15px' }}>Content</small></div>
        <Link to="/admin/profile" className={isActive('/admin/profile')}><FaUser /> Site Profile</Link>
        <Link to="/admin/services" className={isActive('/admin/services')}><FaLayerGroup /> Services CRUD</Link>
        <Link to="/admin/projects" className={isActive('/admin/projects')}><FaProjectDiagram /> Projects CRUD</Link>
        <Link to="/admin/skills" className={isActive('/admin/skills')}><FaCogs /> Skills</Link>
        <Link to="/admin/experience" className={isActive('/admin/experience')}><FaBriefcase /> Experience</Link>
        <Link to="/admin/blog" className={isActive('/admin/blog')}><FaBlog /> Blog Posts</Link>
        <Link to="/admin/testimonials" className={isActive('/admin/testimonials')}><FaQuoteLeft /> Testimonials</Link>
        <Link to="/admin/faq" className={isActive('/admin/faq')}><FaQuestionCircle /> FAQ</Link>
        <Link to="/admin/announcements" className={isActive('/admin/announcements')}><FaBell /> Announcements</Link>
        <div style={{ borderTop: '1px solid var(--border)', margin: '10px 0', paddingTop: '5px' }}><small style={{ color: 'var(--text-muted)', paddingLeft: '15px' }}>Settings</small></div>
        <Link to="/admin/payment-config" className={isActive('/admin/payment-config')}><FaCreditCard /> Payment Config</Link>
        <Link to="/admin/site-settings" className={isActive('/admin/site-settings')}><FaWrench /> Site Settings</Link>
        <Link to="/admin/appearance" className={isActive('/admin/appearance')}><FaPalette /> Appearance</Link>
        <div style={{ borderTop: '1px solid var(--border)', margin: '15px 0' }} />
        <Link to="/"><FaHome /> View Site</Link>
        <a href="#logout" onClick={e => { e.preventDefault(); logout(); navigate('/admin/login'); }} style={{ color: '#ff4757' }}><FaSignOutAlt /> Logout</a>
      </aside>
      <main className="admin-main">
        <Routes>
          <Route path="/" element={<AdminHome />} />
          <Route path="/orders" element={<OrdersManagement />} />
          <Route path="/orders/:id" element={<AdminOrderDetail />} />
          <Route path="/messages" element={<MessagesSection />} />
          <Route path="/profile" element={<ProfileSection />} />
          <Route path="/services" element={
            <CrudSection title="Service" apiPath="/services"
              fields={[{ key: 'title', label: 'Title' }, { key: 'price', label: 'Price', render: v => `$${v}` }, { key: 'deliveryTime', label: 'Days' }, { key: 'isActive', label: 'Active', render: v => v ? 'Yes' : 'No' }]}
              renderForm={(d, set) => (<>
                <div className="form-row"><div className="form-group"><label>Title *</label><input type="text" value={d.title || ''} onChange={e => set({ ...d, title: e.target.value })} required /></div>
                  <div className="form-group"><label>Category *</label><SelectInput value={d.category || ''} onChange={e => set({ ...d, category: e.target.value })} options={[{ value: '', label: '-- Select --' }, ...categories.map(c => ({ value: c._id, label: c.name }))]} /></div></div>
                <div className="form-group"><label>Description *</label><textarea value={d.description || ''} onChange={e => set({ ...d, description: e.target.value })} required /></div>
                <div className="form-group"><label>Short Description</label><input type="text" value={d.shortDescription || ''} onChange={e => set({ ...d, shortDescription: e.target.value })} /></div>
                <div className="form-row"><div className="form-group"><label>Price ($) *</label><input type="number" value={d.price || ''} onChange={e => set({ ...d, price: Number(e.target.value) })} required /></div>
                  <div className="form-group"><label>Discount Price ($)</label><input type="number" value={d.discountPrice || ''} onChange={e => set({ ...d, discountPrice: Number(e.target.value) })} /></div>
                  <div className="form-group"><label>Delivery Days *</label><input type="number" value={d.deliveryTime || 3} onChange={e => set({ ...d, deliveryTime: Number(e.target.value) })} required /></div></div>
                <div className="form-group"><label>Features (comma separated)</label><input type="text" value={Array.isArray(d.features) ? d.features.join(', ') : d.features || ''} onChange={e => set({ ...d, features: e.target.value.split(',').map(t => t.trim()) })} /></div>
                <div className="form-row"><div className="form-group"><label>Featured</label><SelectInput value={d.isFeatured ? 'true' : 'false'} onChange={e => set({ ...d, isFeatured: e.target.value === 'true' })} options={[{ value: 'false', label: 'No' }, { value: 'true', label: 'Yes' }]} /></div>
                  <div className="form-group"><label>Active</label><SelectInput value={d.isActive !== false ? 'true' : 'false'} onChange={e => set({ ...d, isActive: e.target.value === 'true' })} options={[{ value: 'true', label: 'Yes' }, { value: 'false', label: 'No' }]} /></div></div>
              </>)} />
          } />
          <Route path="/categories" element={
            <CrudSection title="Category" apiPath="/categories"
              fields={[{ key: 'name', label: 'Name' }, { key: 'slug', label: 'Slug' }, { key: 'isActive', label: 'Active', render: v => v ? 'Yes' : 'No' }]}
              renderForm={(d, set) => (<>
                <div className="form-row"><div className="form-group"><label>Name *</label><input type="text" value={d.name || ''} onChange={e => set({ ...d, name: e.target.value, slug: e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '-') })} required /></div>
                  <div className="form-group"><label>Slug</label><input type="text" value={d.slug || ''} onChange={e => set({ ...d, slug: e.target.value })} /></div></div>
                <div className="form-group"><label>Description</label><textarea value={d.description || ''} onChange={e => set({ ...d, description: e.target.value })} /></div>
              </>)} />
          } />
          <Route path="/coupons" element={
            <CrudSection title="Coupon" apiPath="/coupons"
              fields={[{ key: 'code', label: 'Code' }, { key: 'discountType', label: 'Type' }, { key: 'discountValue', label: 'Value' }, { key: 'usedCount', label: 'Used' }, { key: 'isActive', label: 'Active', render: v => v ? 'Yes' : 'No' }]}
              renderForm={(d, set) => (<>
                <div className="form-row"><div className="form-group"><label>Code *</label><input type="text" value={d.code || ''} onChange={e => set({ ...d, code: e.target.value.toUpperCase() })} required placeholder="SAVE20" /></div>
                  <div className="form-group"><label>Type</label><SelectInput value={d.discountType || 'percentage'} onChange={e => set({ ...d, discountType: e.target.value })} options={[{ value: 'percentage', label: 'Percentage %' }, { value: 'fixed', label: 'Fixed $' }]} /></div></div>
                <div className="form-row"><div className="form-group"><label>Discount Value *</label><input type="number" value={d.discountValue || ''} onChange={e => set({ ...d, discountValue: Number(e.target.value) })} required /></div>
                  <div className="form-group"><label>Min Order ($)</label><input type="number" value={d.minOrderAmount || 0} onChange={e => set({ ...d, minOrderAmount: Number(e.target.value) })} /></div>
                  <div className="form-group"><label>Max Uses (0=unlimited)</label><input type="number" value={d.maxUses || 0} onChange={e => set({ ...d, maxUses: Number(e.target.value) })} /></div></div>
                <div className="form-group"><label>Valid Until *</label><input type="date" value={d.validUntil ? d.validUntil.substring(0, 10) : ''} onChange={e => set({ ...d, validUntil: e.target.value })} required /></div>
              </>)} />
          } />
          <Route path="/clients" element={
            <CrudSection title="Client" apiPath="/auth/clients"
              fields={[{ key: 'name', label: 'Name' }, { key: 'email', label: 'Email' }, { key: 'totalOrders', label: 'Orders' }, { key: 'totalSpent', label: 'Spent', render: v => `$${v || 0}` }]}
              renderForm={() => <p>Clients register themselves</p>} />
          } />
          <Route path="/projects" element={
            <CrudSection title="Project" apiPath="/projects"
              fields={[{ key: 'title', label: 'Title' }, { key: 'technologies', label: 'Tech' }]}
              renderForm={(d, set) => (<>
                <div className="form-row"><div className="form-group"><label>Title</label><input type="text" value={d.title || ''} onChange={e => set({ ...d, title: e.target.value })} required /></div>
                  <div className="form-group"><label>Technologies (comma)</label><input type="text" value={Array.isArray(d.technologies) ? d.technologies.join(', ') : ''} onChange={e => set({ ...d, technologies: e.target.value.split(',').map(t => t.trim()) })} /></div></div>
                <div className="form-group"><label>Description</label><textarea value={d.description || ''} onChange={e => set({ ...d, description: e.target.value })} required /></div>
                <div className="form-row"><div className="form-group"><label>Live URL</label><input type="text" value={d.liveUrl || ''} onChange={e => set({ ...d, liveUrl: e.target.value })} /></div>
                  <div className="form-group"><label>GitHub URL</label><input type="text" value={d.githubUrl || ''} onChange={e => set({ ...d, githubUrl: e.target.value })} /></div></div>
              </>)} />
          } />
          <Route path="/skills" element={
            <CrudSection title="Skill" apiPath="/skills"
              fields={[{ key: 'name', label: 'Name' }, { key: 'category', label: 'Category' }, { key: 'proficiency', label: '%' }]}
              renderForm={(d, set) => (<>
                <div className="form-row"><div className="form-group"><label>Name</label><input type="text" value={d.name || ''} onChange={e => set({ ...d, name: e.target.value })} required /></div>
                  <div className="form-group"><label>Category</label><SelectInput value={d.category || 'frontend'} onChange={e => set({ ...d, category: e.target.value })} options={['frontend', 'backend', 'database', 'tools', 'other'].map(c => ({ value: c, label: c }))} /></div></div>
                <div className="form-group"><label>Proficiency ({d.proficiency || 80}%)</label><input type="range" min="0" max="100" value={d.proficiency || 80} onChange={e => set({ ...d, proficiency: parseInt(e.target.value) })} style={{ width: '100%' }} /></div>
              </>)} />
          } />
          <Route path="/experience" element={
            <CrudSection title="Experience" apiPath="/experience"
              fields={[{ key: 'position', label: 'Position' }, { key: 'company', label: 'Company' }, { key: 'startDate', label: 'Start' }]}
              renderForm={(d, set) => (<>
                <div className="form-row"><div className="form-group"><label>Position</label><input type="text" value={d.position || ''} onChange={e => set({ ...d, position: e.target.value })} required /></div>
                  <div className="form-group"><label>Company</label><input type="text" value={d.company || ''} onChange={e => set({ ...d, company: e.target.value })} required /></div></div>
                <div className="form-row"><div className="form-group"><label>Start</label><input type="text" value={d.startDate || ''} onChange={e => set({ ...d, startDate: e.target.value })} required placeholder="2023" /></div>
                  <div className="form-group"><label>End</label><input type="text" value={d.endDate || ''} onChange={e => set({ ...d, endDate: e.target.value })} placeholder="Present" /></div></div>
                <div className="form-group"><label>Description</label><textarea value={d.description || ''} onChange={e => set({ ...d, description: e.target.value })} /></div>
              </>)} />
          } />
          <Route path="/tickets" element={<TicketsManagement />} />
          <Route path="/tickets/:id" element={<AdminTicketDetail />} />
          <Route path="/blog" element={
            <CrudSection title="Blog Post" apiPath="/blog/all" writeApiPath="/blog"
              fields={[{ key: 'title', label: 'Title' }, { key: 'category', label: 'Category' }, { key: 'views', label: 'Views' }, { key: 'isPublished', label: 'Published', render: v => v ? 'Yes' : 'No' }]}
              renderForm={(d, set) => (<>
                <div className="form-group"><label>Title *</label><input type="text" value={d.title || ''} onChange={e => set({ ...d, title: e.target.value })} required /></div>
                <div className="form-row">
                  <div className="form-group"><label>Category</label><input type="text" value={d.category || 'General'} onChange={e => set({ ...d, category: e.target.value })} /></div>
                  <div className="form-group"><label>Tags (comma)</label><input type="text" value={Array.isArray(d.tags) ? d.tags.join(', ') : ''} onChange={e => set({ ...d, tags: e.target.value.split(',').map(t => t.trim()) })} /></div>
                </div>
                <div className="form-group"><label>Content *</label><textarea value={d.content || ''} onChange={e => set({ ...d, content: e.target.value })} required style={{ minHeight: '200px' }} /></div>
                <div className="form-group"><label>Published</label><SelectInput value={d.isPublished ? 'true' : 'false'} onChange={e => set({ ...d, isPublished: e.target.value === 'true' })} options={[{ value: 'false', label: 'Draft' }, { value: 'true', label: 'Published' }]} /></div>
              </>)} />
          } />
          <Route path="/testimonials" element={
            <CrudSection title="Testimonial" apiPath="/testimonials"
              fields={[{ key: 'name', label: 'Name' }, { key: 'company', label: 'Company' }, { key: 'rating', label: 'Rating' }, { key: 'isActive', label: 'Active', render: v => v ? 'Yes' : 'No' }]}
              renderForm={(d, set) => (<>
                <div className="form-row">
                  <div className="form-group"><label>Name *</label><input type="text" value={d.name || ''} onChange={e => set({ ...d, name: e.target.value })} required /></div>
                  <div className="form-group"><label>Position</label><input type="text" value={d.position || ''} onChange={e => set({ ...d, position: e.target.value })} /></div>
                  <div className="form-group"><label>Company</label><input type="text" value={d.company || ''} onChange={e => set({ ...d, company: e.target.value })} /></div>
                </div>
                <div className="form-group"><label>Comment *</label><textarea value={d.comment || ''} onChange={e => set({ ...d, comment: e.target.value })} required /></div>
                <div className="form-row">
                  <div className="form-group"><label>Rating</label><SelectInput value={d.rating || 5} onChange={e => set({ ...d, rating: Number(e.target.value) })} options={[1,2,3,4,5].map(n => ({ value: n, label: `${n} Star${n>1?'s':''}` }))} /></div>
                  <div className="form-group"><label>Featured</label><SelectInput value={d.isFeatured ? 'true' : 'false'} onChange={e => set({ ...d, isFeatured: e.target.value === 'true' })} options={[{ value: 'false', label: 'No' }, { value: 'true', label: 'Yes' }]} /></div>
                </div>
              </>)} />
          } />
          <Route path="/faq" element={
            <CrudSection title="FAQ" apiPath="/faq"
              fields={[{ key: 'question', label: 'Question' }, { key: 'category', label: 'Category' }, { key: 'isActive', label: 'Active', render: v => v ? 'Yes' : 'No' }]}
              renderForm={(d, set) => (<>
                <div className="form-group"><label>Question *</label><input type="text" value={d.question || ''} onChange={e => set({ ...d, question: e.target.value })} required /></div>
                <div className="form-group"><label>Answer *</label><textarea value={d.answer || ''} onChange={e => set({ ...d, answer: e.target.value })} required /></div>
                <div className="form-group"><label>Category</label><input type="text" value={d.category || 'General'} onChange={e => set({ ...d, category: e.target.value })} /></div>
              </>)} />
          } />
          <Route path="/announcements" element={
            <CrudSection title="Announcement" apiPath="/announcements/all" writeApiPath="/announcements"
              fields={[{ key: 'message', label: 'Message' }, { key: 'isActive', label: 'Active', render: v => v ? 'Yes' : 'No' }]}
              renderForm={(d, set) => (<>
                <div className="form-group"><label>Message *</label><input type="text" value={d.message || ''} onChange={e => set({ ...d, message: e.target.value })} required placeholder="Special offer! 20% off all services" /></div>
                <div className="form-row">
                  <div className="form-group"><label>Link URL</label><input type="text" value={d.link || ''} onChange={e => set({ ...d, link: e.target.value })} placeholder="/services" /></div>
                  <div className="form-group"><label>Link Text</label><input type="text" value={d.linkText || ''} onChange={e => set({ ...d, linkText: e.target.value })} placeholder="Learn More" /></div>
                </div>
                <div className="form-row">
                  <div className="form-group"><label>Background Color</label>
                    <div style={{ display: 'flex', gap: '8px' }}><input type="color" value={d.bgColor || '#6c63ff'} onChange={e => set({ ...d, bgColor: e.target.value })} style={{ width: '50px', height: '38px', border: 'none' }} /><input type="text" value={d.bgColor || '#6c63ff'} onChange={e => set({ ...d, bgColor: e.target.value })} /></div></div>
                  <div className="form-group"><label>Text Color</label>
                    <div style={{ display: 'flex', gap: '8px' }}><input type="color" value={d.textColor || '#ffffff'} onChange={e => set({ ...d, textColor: e.target.value })} style={{ width: '50px', height: '38px', border: 'none' }} /><input type="text" value={d.textColor || '#ffffff'} onChange={e => set({ ...d, textColor: e.target.value })} /></div></div>
                </div>
                <div className="form-row">
                  <div className="form-group"><label>Active</label><SelectInput value={d.isActive !== false ? 'true' : 'false'} onChange={e => set({ ...d, isActive: e.target.value === 'true' })} options={[{ value: 'true', label: 'Yes' }, { value: 'false', label: 'No' }]} /></div>
                  <div className="form-group"><label>Closable</label><SelectInput value={d.closable !== false ? 'true' : 'false'} onChange={e => set({ ...d, closable: e.target.value === 'true' })} options={[{ value: 'true', label: 'Yes' }, { value: 'false', label: 'No' }]} /></div>
                </div>
              </>)} />
          } />
          {/* Page-by-Page Management */}
          <Route path="/page/home" element={<HomePageManager />} />
          <Route path="/page/about" element={<AboutPageManager />} />
          <Route path="/page/contact" element={<ContactPageManager />} />
          <Route path="/page/resume" element={<ResumePageManager />} />
          <Route path="/page/services" element={<ServicesPageManager />} />
          <Route path="/page/projects" element={<ProjectsPageManager />} />
          <Route path="/page/blog" element={<BlogPageManager />} />
          <Route path="/payment-config" element={<PaymentConfig />} />
          <Route path="/site-settings" element={<SiteSettingsPage />} />
          <Route path="/appearance" element={<AppearancePage />} />
        </Routes>
      </main>
    </div>
  );
};

export default AdminDashboard;
